import { industryCodeToSector } from "./bicsSector";
import { calculateReturnFromSeries, dateToIsoString, dayDiff } from "./functions";

export interface SectorInstrumentPerformance {
    instrumentId?: string | null;
    values: number[];
    cashFlows: number[];
    series: number[];
    instrument?: {
        name?: string | null;
        longName?: string | null;
        bloombergTicker?: string | null;
        modelType?: string | null;
        currency?: string | null;
        issuer?: {
            industryCode?: string | null;
        } | null;
    } | null;
}

export interface StockPeriodReturn {
    perf: number | null;
    sekPerf: number | null;
    /** The period perf and sekPerf actually cover, which is not necessarily the one that was asked for. */
    coveredFrom: string | null;
    coveredTo: string | null;
    /** Last Return(Total) observation the instrument has at all, also outside the period. */
    latestDate: string | null;
}

/** A compounded return together with the period it actually covers. */
export interface PeriodReturn {
    perf: number | null;
    coveredFrom: string | null;
    coveredTo: string | null;
}

const noPeriodReturn: PeriodReturn = { perf: null, coveredFrom: null, coveredTo: null };

export interface SectorTableInput {
    dates: string[];
    values: number[];
    series: number[];
    instrumentPerformances: SectorInstrumentPerformance[];
    stockReturnsByInstrumentId: Record<string, StockPeriodReturn>;
    startDate: string;
    endDate: string;
    /** Instruments owned by the client itself. The Overview tab shows no account return for these. */
    partyInstrumentIds?: string[];
}

export interface InstrumentTableRow {
    key: string;
    instrumentId: string;
    name: string;
    bloombergTicker: string;
    modelType: string;
    sector: string;
    currency: string;
    weightStart: number;
    weightEnd: number;
    contributing: number;
    accountPerf: number | null;
    sekPerf: number | null;
    perf: number | null;
    /** Period the Return(Total) observations behind sekPerf and perf actually cover. */
    perfFrom: string | null;
    perfTo: string | null;
    /** Last Return(Total) observation for the instrument, also outside the period. Null when it has no series. */
    perfLatest: string | null;
}

export const toDateKey = (date: string | Date): string => String(date).substring(0, 10);

export const dateIndexOnOrBefore = (dates: string[], date: string): number => {
    const target = toDateKey(date);
    let index = 0;
    for (let i = 0; i < dates.length; i++) {
        if (toDateKey(dates[i]) <= target) {
            index = i;
        }
    }
    return index;
};

const parseSeriesNumber = (value: string | number): number | null => {
    const n = typeof value === "number" ? value : parseFloat(String(value).replace(/\s/g, "").replace(",", "."));
    if (Number.isFinite(n)) {
        return n;
    }
    return null;
};

export const valueOnOrBefore = (dates: string[], values: Array<string | number>, date: string): number | null => {
    if (!dates || !values) {
        return null;
    }
    const target = toDateKey(date);
    for (let i = dates.length - 1; i >= 0; i--) {
        if (toDateKey(dates[i]) <= target) {
            const n = parseSeriesNumber(values[i]);
            if (n !== null) {
                return n;
            }
        }
    }
    return null;
};

/**
 * Compound Return(Total) daily returns for dates after startDate through endDate.
 *
 * Every value is the return between the previous observation and its own date, so the period a compounded
 * return covers starts at the observation before the first one compounded, not at the first one itself. That
 * observation is looked for in `dates` and, for series too sparse to have one in the fetched window, taken
 * from `anchorDate`. When the series has none at all, its earliest observation is the first one it has: how
 * far back that one reaches is not stored, so it cannot be used and is dropped.
 *
 * The covered period is reported alongside the return, because a series covering only part of what was asked
 * for still compounds to a number and the number alone does not reveal that.
 */
export const compoundDailyReturns = (
    dates: string[],
    values: Array<string | number>,
    startDate: string,
    endDate: string,
    /** Last observation on or before startDate, when known from outside `dates`. */
    anchorDate: string | null = null
): PeriodReturn => {
    if (!dates || !values) {
        return noPeriodReturn;
    }
    const start = toDateKey(startDate);
    const end = toDateKey(endDate);

    let coveredFrom: string | null = anchorDate ? toDateKey(anchorDate) : null;
    const observations: { date: string; value: number }[] = [];
    for (let i = 0; i < dates.length; i++) {
        const date = toDateKey(dates[i]);
        if (date <= start) {
            if (coveredFrom === null || date > coveredFrom) {
                coveredFrom = date;
            }
            continue;
        }
        if (date > end) {
            continue;
        }
        const value = parseSeriesNumber(values[i]);
        if (value === null) {
            continue;
        }
        observations.push({ date, value });
    }
    observations.sort((a, b) => a.date.localeCompare(b.date));

    if (coveredFrom === null) {
        const unusable = observations.shift();
        if (!unusable) {
            return noPeriodReturn;
        }
        coveredFrom = unusable.date;
    }
    if (!observations.length) {
        return noPeriodReturn;
    }

    let product = 1;
    for (const observation of observations) {
        product *= 1 + observation.value;
    }
    return { perf: product - 1, coveredFrom, coveredTo: observations[observations.length - 1].date };
};

// The observations bounding a period land a few days outside or inside it whenever it opens or closes on a
// weekend or a holiday. Long enough for the Easter and Christmas closures, the widest gaps in a daily series.
const COVERAGE_SLACK_DAYS = 7;

const daysBetween = (from: string, to: string): number =>
    (Date.parse(`${toDateKey(to)}T12:00:00.000Z`) - Date.parse(`${toDateKey(from)}T12:00:00.000Z`)) / 86400000;

export interface PeriodCoverage {
    /**
     * `exact` when the observations fall on both ends of the period, `close` when they are within the slack,
     * so the return is still worth showing, and `off` when they are further away than that.
     */
    match: "exact" | "close" | "off";
    /** Which dates the observations do cover, phrased for the reader. Null when they cover the period exactly. */
    note: string | null;
}

/**
 * How well the observations behind a compounded return measure the period that was asked for.
 *
 * A return is only the return for exactly the period asked for when the series is observed on both of its
 * ends. Observations a few days out, which is what a period opening or closing on a weekend or a holiday
 * gives, put the return over a period close to it, and observations further out put it over another period
 * altogether.
 */
export const periodCoverage = (
    coveredFrom: string | null,
    coveredTo: string | null,
    startDate: string,
    endDate: string,
    slackDays: number = COVERAGE_SLACK_DAYS
): PeriodCoverage => {
    if (!coveredFrom || !coveredTo) {
        return { match: "off", note: null };
    }
    // Positive when the observation is before the date it bounds, negative when after it.
    const atStart = daysBetween(coveredFrom, startDate);
    const atEnd = daysBetween(coveredTo, endDate);
    if (atStart === 0 && atEnd === 0) {
        return { match: "exact", note: null };
    }
    if (Math.abs(atStart) > slackDays || Math.abs(atEnd) > slackDays) {
        return {
            match: "off",
            note: `Total return data covers ${coveredFrom} to ${coveredTo}, more than ${slackDays} days off ${startDate} to ${endDate}, so no return is shown for the period.`
        };
    }
    return {
        match: "close",
        note: `Total return data covers ${coveredFrom} to ${coveredTo}, so this is the return over that period and not exactly ${startDate} to ${endDate}.`
    };
};

export interface InstrumentTimeSeries {
    type?: string | null;
    dates?: string[] | null;
    values?: Array<string | number> | null;
}

/**
 * Return(Total) only. Price(Close) is deliberately not used as a fallback: it ignores dividends, so it
 * would put a price return in the same column as total returns without the reader being able to tell.
 * An instrument without total return in the period reports null and renders as a blank cell.
 */
export const stockReturnFromTimeSeries = (
    seriesList: InstrumentTimeSeries[] | null | undefined,
    startDate: string,
    endDate: string,
    anchorDate: string | null = null
): PeriodReturn => {
    if (!seriesList || !seriesList.length) {
        return noPeriodReturn;
    }
    let totalReturnSeries: InstrumentTimeSeries | null = null;
    for (const series of seriesList) {
        if (series.type === "Return(Total)") {
            totalReturnSeries = series;
        }
    }
    if (!totalReturnSeries) {
        return noPeriodReturn;
    }
    return compoundDailyReturns(totalReturnSeries.dates ?? [], totalReturnSeries.values ?? [], startDate, endDate, anchorDate);
};

/**
 * The account return the Overview tab shows for one instrument row, i.e. the portal's own return on the
 * position rather than the instrument's market return.
 *
 * Same path as accountReturn() in functions.ts for a leaf row: the return series is indexed by the
 * calendar day difference between the dates (performance.dates has one entry per calendar day).
 *
 * Always a simple period return. The Overview column annualizes to a CAGR over a year, but the rest of
 * this table (Contributing and the two Performance columns) is simple, and a single annualized column in
 * an export pasted into Excel is not comparable with the ones beside it.
 */
export const accountPerformanceFromSeries = (
    series: number[] | null | undefined,
    dates: string[],
    startDate: string,
    endDate: string
): number | null => {
    if (!Array.isArray(series) || !series.length || !dates || !dates.length) {
        return null;
    }
    const firstDate = new Date(startDate);
    const lastDate = new Date(endDate);
    const diff = Math.abs(dayDiff(firstDate, lastDate));
    const endDateString = dateToIsoString(lastDate);
    let endDateIndex = dates.findIndex((d) => toDateKey(d) === endDateString);
    // If not found start from end
    if (endDateIndex === -1) {
        endDateIndex = dates.length - 1;
    }
    if (endDateIndex - diff < 0) {
        return null;
    }
    const calculatedReturn = calculateReturnFromSeries(series, diff, endDateIndex);
    if (calculatedReturn === null || !Number.isFinite(calculatedReturn)) {
        return null;
    }
    return calculatedReturn;
};

// Attribution waterfall contribution per group, same path as Attribution.tsx dataToTraces.
const contributingByGroup = (
    portfolioValues: number[],
    portfolioSeries: number[],
    instrumentPerformances: SectorInstrumentPerformance[],
    groupOf: (index: number) => string,
    groupKeys: string[],
    startDateIndex: number,
    endDateIndex: number
): Record<string, number> => {
    const groupPerformances: Record<string, { totalReturnDay0: number; totalReturn: number; series: number[] }> = {};
    for (const key of groupKeys) {
        groupPerformances[key] = { totalReturnDay0: 0, totalReturn: 0, series: [] };
    }

    const startSeries = portfolioSeries[startDateIndex];
    const seriesFactor = startSeries === 0 ? 1 : 1 / startSeries;

    for (let i = startDateIndex; i <= endDateIndex; i++) {
        for (let j = 0; j < instrumentPerformances.length; j++) {
            const instrumentPerformance = instrumentPerformances[j];
            const group = groupPerformances[groupOf(j)];
            const cashFlow = instrumentPerformance.cashFlows?.[i] ?? 0;
            const value = instrumentPerformance.values?.[i] ?? 0;
            if (i === 0) {
                group.totalReturn += value - cashFlow;
            } else {
                group.totalReturn += value - (instrumentPerformance.values?.[i - 1] ?? 0) - cashFlow;
            }
        }
        for (const key of groupKeys) {
            let seriesValue = 0;
            if (i === 0) {
                groupPerformances[key].totalReturnDay0 = groupPerformances[key].totalReturn;
                seriesValue = 0;
            } else if (i === startDateIndex) {
                seriesValue = 0;
            } else {
                let totalReturn = groupPerformances[key].totalReturn;
                if (i === 1) {
                    totalReturn += groupPerformances[key].totalReturnDay0;
                }
                if (portfolioValues[i - 1] === 0) {
                    seriesValue = groupPerformances[key].series[groupPerformances[key].series.length - 1] ?? 0;
                } else {
                    const previous = groupPerformances[key].series[groupPerformances[key].series.length - 1] ?? 0;
                    seriesValue = previous + (seriesFactor * portfolioSeries[i - 1] * totalReturn) / portfolioValues[i - 1];
                }
            }
            groupPerformances[key].series.push(seriesValue);
            groupPerformances[key].totalReturn = 0;
        }
    }

    const result: Record<string, number> = {};
    for (const key of groupKeys) {
        const series = groupPerformances[key].series;
        result[key] = series.length ? series[series.length - 1] : 0;
    }
    return result;
};

const instrumentName = (row: SectorInstrumentPerformance): string => {
    const longName = row.instrument?.longName;
    if (longName) {
        return longName;
    }
    return row.instrument?.name || "";
};

export const buildInstrumentRows = ({
    dates,
    values,
    series,
    instrumentPerformances,
    stockReturnsByInstrumentId,
    startDate,
    endDate,
    partyInstrumentIds
}: SectorTableInput): InstrumentTableRow[] => {
    if (!dates.length || !instrumentPerformances.length) {
        return [];
    }

    const startDateIndex = dateIndexOnOrBefore(dates, startDate);
    const endDateIndex = dateIndexOnOrBefore(dates, endDate);
    if (endDateIndex < startDateIndex) {
        return [];
    }

    const keys: string[] = [];
    for (let j = 0; j < instrumentPerformances.length; j++) {
        keys.push(String(j));
    }

    const contributing = contributingByGroup(
        values,
        series,
        instrumentPerformances,
        (index) => keys[index],
        keys,
        startDateIndex,
        endDateIndex
    );

    const isPartyInstrument: Record<string, boolean> = {};
    for (const id of partyInstrumentIds ?? []) {
        isPartyInstrument[String(id)] = true;
    }

    const portfolioStartValue = values[startDateIndex] || 0;
    const portfolioEndValue = values[endDateIndex] || 0;
    const rows: InstrumentTableRow[] = [];
    for (let j = 0; j < instrumentPerformances.length; j++) {
        const instrumentPerformance = instrumentPerformances[j];
        const startValue = instrumentPerformance.values[startDateIndex] || 0;
        const endValue = instrumentPerformance.values[endDateIndex] || 0;
        const instrumentId = instrumentPerformance.instrumentId ? String(instrumentPerformance.instrumentId) : "";
        const stockReturn = instrumentId ? stockReturnsByInstrumentId[instrumentId] : undefined;
        const weightStart = portfolioStartValue === 0 ? 0 : startValue / portfolioStartValue;
        const weightEnd = portfolioEndValue === 0 ? 0 : endValue / portfolioEndValue;
        if (weightStart === 0 && weightEnd === 0) {
            continue;
        }
        rows.push({
            key: `${instrumentId}-${j}`,
            instrumentId,
            name: instrumentName(instrumentPerformance),
            bloombergTicker: instrumentPerformance.instrument?.bloombergTicker || "",
            modelType: instrumentPerformance.instrument?.modelType || "",
            sector: industryCodeToSector(instrumentPerformance.instrument?.issuer?.industryCode),
            currency: instrumentPerformance.instrument?.currency || "",
            weightStart,
            weightEnd,
            contributing: contributing[keys[j]] ?? 0,
            // Rendered as the "Total return" column. Instruments issued by the client itself show N.A. in the
            // Overview tab, so no return is shown here either.
            accountPerf: isPartyInstrument[instrumentId]
                ? null
                : accountPerformanceFromSeries(instrumentPerformance.series, dates, startDate, endDate),
            sekPerf: stockReturn?.sekPerf ?? null,
            perf: stockReturn?.perf ?? null,
            perfFrom: stockReturn?.coveredFrom ?? null,
            perfTo: stockReturn?.coveredTo ?? null,
            perfLatest: stockReturn?.latestDate ?? null
        });
    }

    return rows.sort((a, b) => a.name.localeCompare(b.name) || a.sector.localeCompare(b.sector));
};

const tsvNumber = (value: number | null): string => {
    if (value === null || typeof value === "undefined" || Number.isNaN(value)) {
        return "";
    }
    if (value === 0) {
        return "0";
    }
    // Avoid scientific notation so Excel does not parse 1e-7 as 0.
    return value.toFixed(10).replace(/\.?0+$/, "");
};

export const instrumentRowsToTsv = (rows: InstrumentTableRow[], startDate: string, endDate: string): string => {
    const header = [
        "Instrument",
        "Ticker",
        "Model type",
        "Sector",
        "Currency",
        "Weight Start",
        "Weight End",
        "Contributing",
        "Total return",
        "Performance (SEK)",
        "Performance (local)"
    ].join("\t");
    const lines = rows.map((row) => {
        // Left empty for the same reason the table leaves the cell empty: a return over a period far from the
        // selected one does not belong in these columns.
        const covers = periodCoverage(row.perfFrom, row.perfTo, startDate, endDate).match !== "off";
        return [
            row.name,
            row.bloombergTicker,
            row.modelType,
            row.sector,
            row.currency,
            tsvNumber(row.weightStart),
            tsvNumber(row.weightEnd),
            tsvNumber(row.contributing),
            tsvNumber(row.accountPerf),
            covers ? tsvNumber(row.sekPerf) : "",
            covers ? tsvNumber(row.perf) : ""
        ].join("\t");
    });
    return [header, ...lines].join("\n");
};
